import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import { useBrandConfig } from '../../../context/BrandConfigContext';

// Custom styles for pagination dots
const paginationStyles = `
.campaign-swiper .swiper-pagination {
    position: relative !important;
    margin-top: 30px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    display:none !important;
}

.campaign-swiper .swiper-pagination-bullet {
    width: 12px !important;
    height: 12px !important;
    background: rgba(255, 255, 255, 0.4) !important;
    border: 2px solid white !important;
    opacity: 1 !important;
    margin: 0 8px !important;
    position: relative !important;
    transition: all 0.3s ease !important;
}

.campaign-swiper .swiper-pagination-bullet-active {
    background: white !important;
    transform: scale(1.2) !important;
}

.campaign-swiper .swiper-pagination-bullet:not(:last-child):after {
    content: '';
    position: absolute;
    top: 50%;
    left: 100%;
    width: 16px;
    height: 2px;
    background: rgba(255, 255, 255, 0.6);
    transform: translateY(-50%);
    z-index: -1;
}

/* "reveal" variant — full-bleed slides with a visible dot rail (bottom-left) instead of the card carousel's hidden pagination */
.reveal-swiper .swiper-pagination {
    position: absolute !important;
    bottom: 16px !important;
    left: 16px !important;
    width: auto !important;
    margin: 0 !important;
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
}

.reveal-swiper .swiper-pagination-bullet {
    width: 7px !important;
    height: 7px !important;
    background: rgba(255, 255, 255, 0.5) !important;
    border: none !important;
    opacity: 1 !important;
    margin: 0 4px 0 0 !important;
    transition: all 0.3s ease !important;
}

.reveal-swiper .swiper-pagination-bullet-active {
    background: #fff !important;
    transform: scale(1.15) !important;
}
`;

// Per-variant defaults — override any of these via props for a one-off configuration.
const VARIANT_CONFIG = {
  card: {
    swiperClassName: 'campaign-swiper',
    slidesPerView: 1.2,
    spaceBetween: 14,
    centeredSlides: true,
    showPagination: false,
    autoplayDelay: 4000
  },
  reveal: {
    swiperClassName: 'reveal-swiper',
    slidesPerView: 1,
    spaceBetween: 0,
    centeredSlides: false,
    showPagination: true,
    autoplayDelay: 3200
  }
};

function Carousel({
  slides,
  variant = 'card',
  slidesPerView,
  spaceBetween,
  centeredSlides,
  showPagination,
  autoplay = true,
  renderSlide,
  className = ''
} = {}) {
  const brand = useBrandConfig();
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.card;

  // Use slides from brand config when available, otherwise fall back to defaults
  const defaultSlides = [
    {
      id: 1,
      image: '/assets/images/campaigns/enter-and-win.png',
      title: brand?.campaign?.name || 'Enter & Win',
      description: 'Enter your unique code and stand a chance to win amazing prizes every day!',
      backgroundColor: 'transparent'
    },
    {
      id: 2,
      image: '/assets/images/campaigns/enter-and-win.png',
      title: 'Provide details below',
      description: 'Every entry brings you closer to amazing prizes – don\'t miss your chance to win!',
      backgroundColor: 'transparent'
    }
  ];

  const campaigns = (slides?.length > 0 ? slides : brand?.carousel_slides?.length > 0 ? brand.carousel_slides : defaultSlides)
    .map(s => ({ ...s, backgroundColor: s.backgroundColor || 'transparent' }));

  return (
    <>
      <style>{paginationStyles}</style>
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={spaceBetween ?? config.spaceBetween}
        slidesPerView={slidesPerView ?? config.slidesPerView}
        centeredSlides={centeredSlides ?? config.centeredSlides}
        autoplay={autoplay ? { delay: config.autoplayDelay, disableOnInteraction: false } : false}
        pagination={
          (showPagination ?? config.showPagination)
            ? { clickable: true, dynamicBullets: false, disableOnInteraction: false }
            : false
        }
        slideToClickedSlide={true}
        className={`${config.swiperClassName} mb-2 ${className}`.trim()}
      >
        {campaigns.map((campaign) => (
          <SwiperSlide key={campaign.id}>
            {renderSlide ? (
              renderSlide(campaign)
            ) : (
              <div className="campaign-card " style={{ backgroundColor: campaign.backgroundColor || '#00000029' }}>
                <div className="campaign-content text-white position-relative overflow-hidden">
                  {/* Campaign Image */}
                  <div className="campaign-image text-center position-relative" style={{ zIndex: 2}}>
                    <img src={campaign.image} alt={campaign.title} className="img-fluid rounded-2" style={{ objectFit: 'contain' }} />
                  </div>

                  <div
                    className="campaign-cover p-4 rounded-4 position-relative"
                    style={{ backgroundColor: '#00000029', paddingTop: '100px', zIndex: 1 }}
                  >
                    <div className="campaign-header mt-2 mb-0">
                      <h3 className="fw-bold mt-1 mb-0" style={{ fontSize: '1.2rem', lineHeight: '1.3' }}>
                        {campaign.title}
                      </h3>
                    </div>

                    <div className="campaign-body mb-3">
                      <p className="mb-0 opacity-90" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                        {campaign.description}
                      </p>
                    </div>
                    {campaign.button && (
                      <div className="campaign-action my-4">
                        <Link
                          to={campaign.button.link}
                          className="btn btn-light btn-md fw-medium px-4 py-2 rounded-pill"
                          style={{ fontSize: '0.8rem' }}
                        >
                          {campaign.button.text}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
}

export default Carousel;

